import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosGateway } from '../../../api/axios';
import { AVAILABILITY_PATHS } from '../../../api/endpoints';

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    return {
      message: data.message || data.error || 'Une erreur est survenue',
      error: data.error || null,
      conflictingSlot: data.conflictingSlot || null,
      statusCode: data.statusCode || error?.response?.status || null,
    };
  }
  return { message: error?.message || 'Une erreur est survenue' };
};

export const createDisponibilite = createAsyncThunk(
  'disponibility/createDisponibilite',
  async ({ medecinId, payload }, { rejectWithValue }) => {
    try {

      const response = await axiosGateway.post(
        AVAILABILITY_PATHS.CREATE_RANGE(medecinId),
        payload
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const fetchAllDoctorSlots = createAsyncThunk(
  'disponibility/fetchAllDoctorSlots',
  async ({ medecinId }, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.get(
        AVAILABILITY_PATHS.GET_ALL_SLOTS(medecinId)
      );
      return { medecinId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const fetchDoctorSlotsByDate = createAsyncThunk(
  'disponibility/fetchDoctorSlotsByDate',
  async ({ medecinId, date }, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.get(
        AVAILABILITY_PATHS.GET_SLOTS_FOR_DAY(medecinId, date)
      );
      return { date, medecinId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const fetchDoctorAvailableSlotsByDate = createAsyncThunk(
  'disponibility/fetchDoctorAvailableSlotsByDate',
  async ({ medecinId, date }, { rejectWithValue }) => {
    try {
      if (!medecinId || !date) {
        throw new Error('medecinId and date are required');
      }
      const response = await axiosGateway.get(
        AVAILABILITY_PATHS.GET_AVAILABLE_SLOTS(medecinId, date)
      );
      return { date, medecinId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const fetchBlockedSlots = createAsyncThunk(
  'disponibility/fetchBlockedSlots',
  async ({ medecinId, date }, { rejectWithValue }) => {
    try {
      if (!medecinId || !date) {
        throw new Error('medecinId and date are required');
      }
      const response = await axiosGateway.get(
        AVAILABILITY_PATHS.GET_BLOCKED_SLOTS(medecinId, date)
      );
      return { medecinId, date, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const updateDisponibilite = createAsyncThunk(
  'disponibility/updateDisponibilite',
  async ({ slotId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.patch(
        AVAILABILITY_PATHS.UPDATE(slotId),
        payload
      );
      return { slotId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const deleteDisponibilite = createAsyncThunk(
  'disponibility/deleteDisponibilite',
  async ({ slotId }, { rejectWithValue }) => {
    try {
      await axiosGateway.delete(AVAILABILITY_PATHS.DELETE(slotId));
      return slotId;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const markRangeUnavailable = createAsyncThunk(
  'disponibility/markRangeUnavailable',
  async ({ medecinId, startTime, endTime, slotDuration }, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.post(
        AVAILABILITY_PATHS.MARK_RANGE_UNAVAILABLE,
        { medecinId, startTime, endTime, slotDuration: slotDuration || 30 }
      );
      return response.data;
    } catch (error) {
      console.error('[THUNK] markRangeUnavailable ✗', error?.response?.data);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
export const markRangeAvailable = createAsyncThunk(
  'disponibility/markRangeAvailable',
  async ({ startTime, endTime }, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.post(
        AVAILABILITY_PATHS.MARK_RANGE_AVAILABLE,
        { startTime, endTime }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const markSlotsUnavailable = createAsyncThunk(
  'disponibility/markSlotsUnavailable',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.post(
        AVAILABILITY_PATHS.MARK_SLOTS_UNAVAILABLE,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);

export const markSlotsAvailable = createAsyncThunk(
  'disponibility/markSlotsAvailable',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosGateway.post(
        AVAILABILITY_PATHS.MARK_SLOTS_AVAILABLE,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || 'Une erreur est survenue' }
      );
    }
  }
);